/**
 * HustleRoundupSchedulePage — schedule a future room.
 *
 * POST /api/hustleroundup/rooms
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Copy, CheckCircle2, ArrowLeft } from "lucide-react";
import { apiFetch, fetchMe } from "@/lib/api";

const KINDS = [
  { value: "scheduled",  label: "Scheduled meeting" },
  { value: "persistent", label: "Persistent room" },
  { value: "instant",    label: "Instant / one-off" },
];

const GUEST_POLICIES = [
  { value: "auth",     label: "Authenticated users only" },
  { value: "link",     label: "Anyone with the link" },
  { value: "disabled", label: "No guests" },
];

export default function HustleRoundupSchedulePage() {
  const nav = useNavigate();
  const [me, setMe]         = useState(null);
  const [name, setName]     = useState("");
  const [kind, setKind]     = useState("scheduled");
  const [start, setStart]   = useState("");
  const [end, setEnd]       = useState("");
  const [tz, setTz]         = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [guestJoin, setGJ]  = useState("auth");
  const [passcode, setPass] = useState("");
  const [waitingRoom, setW] = useState(true);
  const [recording, setRec] = useState("off");
  const [busy, setBusy]     = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    (async () => {
      const u = await fetchMe();
      if (!u?.id) { nav("/login?next=/schedule"); return; }
      setMe(u);
    })();
  }, [nav]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { toast.error("Give the room a name"); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        kind,
        scheduled_start: start ? new Date(start).toISOString() : null,
        scheduled_end:   end   ? new Date(end).toISOString()   : null,
        timezone: tz,
        guest_join: guestJoin,
        passcode: passcode || null,
        waiting_room: waitingRoom,
        recording,
      };
      const room = await apiFetch("/hustleroundup/rooms", {
        method: "POST", body: JSON.stringify(payload),
      });
      setCreated(room);
      toast.success("Room scheduled");
    } catch (err) {
      toast.error(err.message || "Failed to schedule");
    } finally {
      setBusy(false);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div data-testid="hustleroundup-schedule"
         className="min-h-screen bg-stone-950 text-white px-6 py-10">
      <div className="max-w-3xl mx-auto">
        <button
          data-testid="schedule-back"
          onClick={() => nav("/")}
          className="text-stone-400 hover:text-white text-sm flex items-center gap-2 mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Calendar className="w-7 h-7 text-emerald-400" />
          <h1 className="text-3xl font-bold">Schedule a meeting</h1>
        </div>
        <p className="text-stone-400 text-sm mb-8 max-w-lg">
          Pick a time, share the join code. Passcode + waiting-room + cloud
          recording all optional per room.
        </p>

        {created ? (
          <CreatedCard room={created} onCopy={copy} onDone={() => setCreated(null)} nav={nav} />
        ) : (
          <form onSubmit={submit} data-testid="hustleroundup-schedule-form"
                className="space-y-5 bg-stone-900/40 border border-stone-900 rounded-2xl p-6">
            <Field label="Meeting name">
              <input
                data-testid="hustleroundup-schedule-name"
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Weekly standup"
                className={inputCls}
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Kind">
                <select
                  data-testid="hustleroundup-schedule-kind"
                  value={kind} onChange={(e) => setKind(e.target.value)}
                  className={inputCls}
                >
                  {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </Field>
              <Field label="Timezone">
                <input
                  data-testid="hustleroundup-schedule-tz"
                  value={tz} onChange={(e) => setTz(e.target.value)} className={inputCls}
                />
              </Field>
              <Field label="Start (local)">
                <input
                  type="datetime-local"
                  data-testid="hustleroundup-schedule-date"
                  value={start} onChange={(e) => setStart(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="End (local)">
                <input
                  type="datetime-local"
                  data-testid="hustleroundup-schedule-time"
                  value={end} onChange={(e) => setEnd(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Guest policy">
                <select
                  data-testid="hustleroundup-schedule-guest"
                  value={guestJoin} onChange={(e) => setGJ(e.target.value)}
                  className={inputCls}
                >
                  {GUEST_POLICIES.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </Field>
              <Field label="Recording">
                <select
                  data-testid="hustleroundup-schedule-recording"
                  value={recording} onChange={(e) => setRec(e.target.value)}
                  className={inputCls}
                >
                  <option value="off">Off</option>
                  <option value="on-if-host">On (host toggles)</option>
                  <option value="always">Always</option>
                </select>
              </Field>
              <Field label="Passcode (optional)">
                <input
                  type="text"
                  data-testid="hustleroundup-schedule-passcode"
                  value={passcode} onChange={(e) => setPass(e.target.value)}
                  placeholder="4-16 chars"
                  className={inputCls}
                />
              </Field>
              <Field label="Waiting room">
                <label className="flex items-center gap-3 pt-2.5 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="hustleroundup-schedule-waiting"
                    checked={waitingRoom}
                    onChange={(e) => setW(e.target.checked)}
                    className="w-4 h-4 accent-emerald-500"
                  />
                  Require host approval before joining
                </label>
              </Field>
            </div>

            <button
              type="submit"
              data-testid="hustleroundup-schedule-submit"
              disabled={busy}
              className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold text-sm disabled:opacity-60"
            >
              {busy ? "Scheduling…" : "Create room"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 rounded-lg bg-stone-950 border border-stone-800 focus:border-emerald-500 outline-none text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-stone-400 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function CreatedCard({ room, onCopy, onDone, nav }) {
  const link    = `${window.location.origin}/join/${room.join_code}`;
  const icsUrl  = `${process.env.REACT_APP_BACKEND_URL}/api/hustleroundup/rooms/${room.slug}/ics`;
  const [sending, setSending] = useState(false);
  const sendInvites = async () => {
    setSending(true);
    try {
      const res = await apiFetch(`/hustleroundup/rooms/${room.slug}/send-invites`, { method: "POST" });
      if (!res.resend_configured) toast.warning("Email service not configured on this pod");
      else toast.success(`Sent ${res.sent} invite${res.sent === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };
  return (
    <div data-testid="hustleroundup-schedule-created"
         className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6">
      <div className="flex items-center gap-2 text-emerald-300 text-sm mb-4">
        <CheckCircle2 className="w-5 h-5" /> Room “{room.name}” created
      </div>
      <div className="grid gap-3">
        <Row label="Join code" value={room.join_code} onCopy={onCopy} testid="created-code" />
        <Row label="Share link" value={link}          onCopy={onCopy} testid="created-link" />
        <Row label="Slug"       value={room.slug}     onCopy={onCopy} testid="created-slug" />
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        <button
          data-testid="schedule-open-room"
          onClick={() => nav(`/meet/${room.slug}`)}
          className="px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold text-sm"
        >
          Open room now
        </button>
        <a
          data-testid="schedule-download-ics"
          href={icsUrl}
          className="px-4 py-2 rounded-full border border-stone-700 hover:border-emerald-500 text-sm"
        >
          Download .ics
        </a>
        <button
          data-testid="schedule-send-invites"
          onClick={sendInvites}
          disabled={sending}
          className="px-4 py-2 rounded-full border border-stone-700 hover:border-emerald-500 text-sm disabled:opacity-60"
        >
          {sending ? "Sending…" : "Send email invites"}
        </button>
        <button
          data-testid="schedule-create-another"
          onClick={onDone}
          className="px-4 py-2 rounded-full border border-stone-700 text-sm"
        >
          Schedule another
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, onCopy, testid }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-[11px] uppercase tracking-widest text-stone-400 w-24">{label}</div>
      <code data-testid={testid} className="flex-1 px-3 py-2 rounded-md bg-stone-950 border border-stone-800 text-sm break-all">
        {value}
      </code>
      <button
        onClick={() => onCopy(value)}
        className="p-2 rounded-md border border-stone-800 text-stone-400 hover:text-white"
        aria-label="Copy"
      ><Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
}
